import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, LayoutGrid, Map } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useApi } from "@/lib/api";
import DestinationCard from "@/components/site/DestinationCard";
import MapView from "@/components/site/MapView";
import { PageHero } from "@/components/site/SectionHead";

const CATS = ["all", "nature", "lake", "beach", "culture", "culinary"];

export default function Destinations() {
  const { t } = useLang();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const cat = params.get("category") || "all";
  const [view, setView] = useState("list");
  const qs = new URLSearchParams({ category: cat, ...(params.get("q") ? { q: params.get("q") } : {}) }).toString();
  const { data, loading } = useApi(`/destinations?${qs}`);
  const update = (k, v) => { const p = new URLSearchParams(params); v && v !== "all" ? p.set(k, v) : p.delete(k); setParams(p); };

  return (
    <div data-testid="destinations-page">
      <PageHero title={t("dest_title")} sub={t("dest_sub")}>
        <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center">
          <form onSubmit={(e) => { e.preventDefault(); update("q", q); }} className="flex flex-1 items-center gap-2 rounded-full border border-slate-200 bg-white p-1.5 pl-4 shadow-sm">
            <Search className="h-5 w-5 text-slate-400" />
            <input data-testid="dest-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_placeholder")} className="flex-1 bg-transparent py-2 outline-none" />
            <button data-testid="dest-search-btn" className="rounded-full bg-[#064E3B] px-5 py-2 text-sm font-bold text-white hover:bg-[#047857]">{t("search")}</button>
          </form>
          <div className="flex rounded-full border border-slate-200 bg-white p-1">
            {[["list", LayoutGrid], ["map", Map]].map(([v, Icon]) => (
              <button key={v} data-testid={`view-${v}-btn`} onClick={() => setView(v)} className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${view === v ? "bg-slate-900 text-white" : "text-slate-600"}`}><Icon className="h-4 w-4" />{t(`${v}_view`)}</button>
            ))}
          </div>
        </div>
        <div className="mt-5 flex gap-2 overflow-x-auto no-scrollbar">
          {CATS.map((c) => (
            <button key={c} data-testid={`cat-${c}-btn`} onClick={() => update("category", c)} className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${cat === c ? "border-[#064E3B] bg-[#064E3B] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`}>{t(`cat_${c}`)}</button>
          ))}
        </div>
      </PageHero>
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {!loading && data?.length === 0 && <p data-testid="no-results" className="py-20 text-center text-slate-500">{t("no_results")}</p>}
        {view === "map" ? <MapView points={(data || []).map((d) => ({ ...d, subtitle: d.city }))} height="h-[620px]" /> : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">{(data || []).map((d, i) => <DestinationCard key={d.slug} d={d} i={i} />)}</div>
        )}
      </section>
    </div>
  );
}
